import Datetime from 'utils/datetime/datetime'
import { getDayHourStarts } from 'utils/datetime/dayTimeSteps'
import type {
  ElplanDraft,
  ElplanHourFlatten,
  ElplanHourReading,
  ElplanShownReading,
  ElplanSlot,
  ElplanSlotEdit,
  ElplanSlotFaults,
} from './ElplanEditor.types'
import { roundElplanVolumeMean, toElplanVolumeCellText } from './volume'

const BLANK_READING = { kind: 'blank' } as const

export function applyElplanSlotEdits(draft: ElplanDraft, edits: readonly ElplanSlotEdit[]): ElplanDraft {
  const draftStarts = new Set(draft.slotStarts)
  const lastTextByStart = new Map(
    edits.filter(({ start }) => draftStarts.has(start)).map(({ start, text }) => [start, text])
  )
  const changes = [...lastTextByStart].filter(([start, text]) => draft.textByStart[start] !== text)
  if (changes.length === 0) {
    return draft
  }

  return { ...draft, textByStart: { ...draft.textByStart, ...Object.fromEntries(changes) } }
}

export function toStoredSlotEdits(slot: ElplanSlot, text: string, draft: ElplanDraft): ElplanSlotEdit[] {
  if (slot.unit === draft.resolution) {
    return [{ start: slot.start, text }]
  }
  if (slot.unit === 'quarter' || !isDraftHourStart(slot.start, draft)) {
    return []
  }

  return getQuarterStartsOfHour(slot.start, draft).map((start) => ({ start, text }))
}

export function getElplanHourValue(
  hourStart: ISODateTime,
  draft: ElplanDraft,
  readingByStart: Readonly<Record<ISODateTime, ElplanShownReading>>
): ElplanHourReading {
  const slotStarts = getElplanHourSlotStarts(hourStart, draft)
  const readings = slotStarts.map((start) => readingByStart[start] ?? BLANK_READING)
  const fault = readings.find((reading) => reading.kind === 'fault')
  if (fault) {
    return fault
  }
  if (readings.some((reading) => reading.kind === 'unfinished')) {
    return { kind: 'unfinished' }
  }

  const quarterVolumesMw = readings.flatMap((reading) => (reading.kind === 'value' ? [reading.volumeMw] : []))
  if (quarterVolumesMw.length === 0) {
    return BLANK_READING
  }
  if (quarterVolumesMw.length < readings.length) {
    return { kind: 'partial', shownMeanMw: roundElplanVolumeMean(quarterVolumesMw) }
  }

  const volumeMw = draft.resolution === 'hour' ? quarterVolumesMw[0] : roundElplanVolumeMean(quarterVolumesMw)
  return { kind: 'value', volumeMw, quarterVolumesMw }
}

export function groupElplanDraftByHour(draft: ElplanDraft): { hourStart: ISODateTime; slotStarts: ISODateTime[] }[] {
  if (draft.resolution === 'hour') {
    return draft.slotStarts.map((start) => ({ hourStart: start, slotStarts: [start] }))
  }

  const dayHourStarts = new Set(draft.slotStarts.length > 0 ? getDayHourStarts(draft.slotStarts[0]) : [])
  return draft.slotStarts
    .filter((start) => dayHourStarts.has(start))
    .map((hourStart) => ({ hourStart, slotStarts: getQuarterStartsOfHour(hourStart, draft) }))
}

export function flattenElplanDraftToHours(draft: ElplanDraft, faults: ElplanSlotFaults): ElplanHourFlatten {
  const partlyBlankHourStarts: ISODateTime[] = []
  const faultedHourStarts: ISODateTime[] = []
  const edits: ElplanSlotEdit[] = []
  let changedHourCount = 0

  for (const { hourStart, slotStarts } of groupElplanDraftByHour(draft)) {
    const hour = getElplanHourValue(hourStart, draft, faults.readingByStart)
    if (hour.kind === 'fault' || hour.kind === 'unfinished') {
      faultedHourStarts.push(hourStart)
    } else if (hour.kind === 'partial') {
      partlyBlankHourStarts.push(hourStart)
    } else if (hour.kind === 'blank') {
      edits.push(...toUniformTextEdits(slotStarts, '', draft))
    } else {
      if (hour.quarterVolumesMw.some((volumeMw) => volumeMw !== hour.volumeMw)) {
        changedHourCount += 1
      }
      edits.push(...toUniformTextEdits(slotStarts, toElplanVolumeCellText(hour.volumeMw), draft))
    }
  }

  if (partlyBlankHourStarts.length > 0 || faultedHourStarts.length > 0) {
    return { kind: 'refused', partlyBlankHourStarts, faultedHourStarts }
  }
  return { kind: 'flattens', edits, changedHourCount }
}

function toUniformTextEdits(slotStarts: readonly ISODateTime[], text: string, draft: ElplanDraft): ElplanSlotEdit[] {
  return slotStarts.filter((start) => draft.textByStart[start] !== text).map((start) => ({ start, text }))
}

function isDraftHourStart(start: ISODateTime, draft: ElplanDraft): boolean {
  return draft.slotStarts.includes(start) && getDayHourStarts(start).includes(start)
}

export function getElplanHourSlotStarts(hourStart: ISODateTime, draft: ElplanDraft): ISODateTime[] {
  if (draft.resolution === 'hour') {
    return [hourStart]
  }
  return isDraftHourStart(hourStart, draft) ? getQuarterStartsOfHour(hourStart, draft) : []
}

function getQuarterStartsOfHour(hourStart: ISODateTime, draft: ElplanDraft): ISODateTime[] {
  const nextHourStart = Datetime.addMinutes(hourStart, 60)
  return draft.slotStarts.filter(
    (start) => Datetime.isAfterOrEqual(start, hourStart) && Datetime.isBefore(start, nextHourStart)
  )
}
